import type { FC } from 'react'
import { useEffect, useMemo } from 'react'
import { Navigate, useLocation, useParams } from 'react-router'
import 'styles/index.scss'

import Header from 'layout/Header/Header'
import { Sidebar } from 'layout/Sidebar/Sidebar'
import { PATH_SEGMENT } from 'routes/pathSegments'
import { generateRouter, PathUtils } from 'routes/routes.utils'

import { useScreenWidth } from 'app/hooks/useScreenWidth'
import { useNotification } from 'app/providers'
import { useAuth } from 'app/providers/auth/AuthProvider'

import { CartWidget } from 'common/components/CartWidget/CartWidget'
import { Show } from 'common/components/Show/Show'

import GenericModal from 'common/components/GenericModal'
import { Spinner } from 'common/components/Spinner'
import { Admin } from 'features/Admin'
import { Cart } from 'features/Cart'
import { useCartManager } from 'features/Cart/hooks/useCartManager'
import { Faq } from 'features/Faq'
import { Home } from 'features/Home'
import { JitsiTelehealthFloatingDialog } from 'features/JitsiTelehealth'
import { useShopCartManager } from 'features/Option/hooks/useShopCartManager'
import { Patients } from 'features/Patients'
import Procedure from 'features/Procedure'
import { Profile } from 'features/Profile/Profile'
import { Provider } from 'features/Provider/pages/Provider'
import { Schedule } from 'features/Schedule'
import { MultiplePusherProvider } from 'features/Schedule/pusher/MultiplePusherProvider'
import { Staff } from 'features/Staff'
import { Support } from 'features/Support/pages/Support'

export const Main: FC = () => {
  const { siteId } = useParams()
  const location = useLocation()
  const { isMobile } = useScreenWidth()
  const { notification, setNotificationScope } = useNotification()
  const { fetchCartsData } = useCartManager()
  const { fetchShopCartData } = useShopCartManager()
  const { toggleIsSidebarVisible, user, isUnconfirmedDoctor, isSidebarVisible, hasEmrEnabled } =
    useAuth()

  const pathSegments = useMemo(
    () => location.pathname.split('/').filter(Boolean),
    [location.pathname],
  )
  const isChartsPage = pathSegments[1]?.toLowerCase() === PATH_SEGMENT.CHARTS
  const isSchedulePage = pathSegments[1]?.toLowerCase() === PATH_SEGMENT.SCHEDULE

  const routesConfig = useMemo(
    () => ({
      [PATH_SEGMENT.FAQ]: <Faq />,
      [PATH_SEGMENT.STAFF]: <Staff />,
      [`${PATH_SEGMENT.ADMIN}/*`]: <Admin />,
      ...(!hasEmrEnabled && {
        [PATH_SEGMENT.PATIENTS]: <Patients />,
      }),
      ...(hasEmrEnabled && {
        [PATH_SEGMENT.PATIENTS]: <Navigate to={PathUtils.getHome(siteId)} replace />,
      }),
      [PATH_SEGMENT.CHARTS]: (
        <MultiplePusherProvider selectedSite={siteId}>
          <Procedure />
        </MultiplePusherProvider>
      ),
      [`${PATH_SEGMENT.HOME}/*`]: <Home />,
      [`${PATH_SEGMENT.PROVIDER}/*`]: <Provider />,
      ...(!isUnconfirmedDoctor && {
        [`${PATH_SEGMENT.PROFILE}/*`]: <Profile />,
      }),
      [`${PATH_SEGMENT.SCHEDULE}/*`]: <Schedule />,
      [`${PATH_SEGMENT.SUPPORT}/*`]: <Support />,
      [PATH_SEGMENT.OTHER]: <Navigate to={PathUtils.getHome(siteId)} replace />,
    }),
    [isUnconfirmedDoctor, hasEmrEnabled, siteId],
  )

  useEffect(() => {
    if (!siteId) {
      return
    }

    fetchCartsData()
    fetchShopCartData()
  }, [siteId])

  useEffect(() => {
    setNotificationScope(isChartsPage ? 'charts' : 'default')

    return () => {
      setNotificationScope('default')
    }
  }, [isChartsPage, setNotificationScope])

  return (
    <Show
      when={!!user}
      fallback={
        <div className='whole-page-wrapper'>
          <Spinner size='large' isLoading />
        </div>
      }>
      <>
        {/* Hide Header only on charts page */}
        <Show when={!isChartsPage}>
          <Header />
        </Show>

        {/* Floating toggle button for charts page only - on left edge */}
        <Show when={isChartsPage}>
          <button
            className='floating-sidebar-toggle'
            data-sidebar-active={isSidebarVisible}
            onClick={toggleIsSidebarVisible}
            aria-label='Toggle Sidebar'>
            <div className='floating-sidebar-toggle__lines'>
              <span className='floating-sidebar-toggle__line' />
              <span className='floating-sidebar-toggle__line' />
              <span className='floating-sidebar-toggle__line' />
            </div>
          </button>
        </Show>

        <Show when={!isMobile && isSidebarVisible}>
          <Sidebar
            isSidebarVisible={isSidebarVisible}
            toggleIsSidebarVisible={toggleIsSidebarVisible}
            isChartsPage={isChartsPage}
          />
        </Show>
        <Show when={isMobile && isSidebarVisible}>
          <Sidebar
            isSidebarVisible={isSidebarVisible}
            toggleIsSidebarVisible={toggleIsSidebarVisible}
            isChartsPage={isChartsPage}
          />
        </Show>

        <div
          className='application-layout__content'
          data-sidebar-visible={isSidebarVisible}
          data-charts-page={isChartsPage}
          data-schedule-page={isSchedulePage}>
          <div
            className='application-layout__content-container'
            onClick={() => isMobile && isSidebarVisible && toggleIsSidebarVisible()}>
            {notification}
            {generateRouter(routesConfig)}

            <Cart />
            <CartWidget hidden={isChartsPage} />
            <GenericModal />
            <JitsiTelehealthFloatingDialog />
          </div>
        </div>
      </>
    </Show>
  )
}
